// ═══════════════════════════════════════════════════════════════════
// ProfileDropdown — Единый компонент профиля для MarkBase App
// Спецификация: UAM v1.5.0 | Account Switcher v1.4.0
// Версия: JSX (CRA / без TypeScript)
// Стили: STYLESYSTEM/interfaces/profile/profile-dropdown.css (классы mb-pdd__*)
// ═══════════════════════════════════════════════════════════════════
import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { ScreenLockContext } from '../../contexts/ScreenLockContext';
import { toastAPI } from '../../contexts/ToastContext';
import {
  UAM_URL,
  BILLING_URL,
  HELP_URL,
} from 'utils/externalLinks';
import { NOTIFICATIONS_APP_INBOX } from '../../constants/notificationsModuleUrls';
import {
  BadgeCheck, Wallet, Percent, ArrowRightLeft,
  Plus, X, User, Bell, HelpCircle,
  ExternalLink, LogOut, Settings, Lock
} from 'lucide-react';
import { pickAvatarDiscriminationHex } from '../../constants/avatarDiscriminationHex';
import { pickSafeRussianBackendMessage, pickHttpStatusRussianFallback } from '../../utils/safeRussianBackendMessage';
import { billingApiPath } from '../../utils/billingApiPaths';

/* ─── Constants ─── */
const ACCOUNTS_KEY = 'wsid_accounts';
const BILLING_API_URL = String(process.env.REACT_APP_BILLING_API_URL || BILLING_URL).replace(/\/+$/, '');

const getBillingApiCandidates = () => {
  const candidates = [];
  if (BILLING_API_URL) candidates.push(BILLING_API_URL);
  if (!candidates.includes(BILLING_URL)) candidates.push(BILLING_URL);
  // Локальный fallback через app backend/proxy.
  candidates.push('');
  return candidates;
};

const fetchBillingJson = async (path) => {
  const candidates = getBillingApiCandidates();
  for (const base of candidates) {
    try {
      const url = base ? `${base}${path}` : path;
      const resp = await fetch(url, { credentials: 'include' });
      let payload = {};
      try {
        const text = await resp.text();
        payload = text ? JSON.parse(text) : {};
      } catch {
        payload = {};
      }
      if (!resp.ok) continue;
      if (typeof payload.success === 'boolean' && payload.success === false) continue;
      return payload;
    } catch (_) {
      // try next endpoint candidate
    }
  }
  return null;
};

/* ─── Avatar helpers ─── */
const getAvatarColor = pickAvatarDiscriminationHex;

const getInitials = (name, email) => {
  if (name && name !== email) {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name[0].toUpperCase();
  }
  return (email || '?')[0].toUpperCase();
};

const maskEmail = (email) => {
  if (!email || !email.includes('@')) return email || '';
  const [local, domain] = email.split('@');
  const masked = local.length <= 2 ? local[0] + '***' : local.slice(0, 2) + '***';
  return masked + '@' + domain;
};

/* ─── LocalStorage helpers ─── */
const safeGet = (key) => { try { return localStorage.getItem(key); } catch (_) { return null; } };
const safeSet = (key, val) => { try { localStorage.setItem(key, val); } catch (_) {} };

const getSavedAccounts = () => {
  try {
    const parsed = JSON.parse(safeGet(ACCOUNTS_KEY) || '[]');
    if (!Array.isArray(parsed)) return [];
    let changed = false;
    const safeAccounts = parsed.map((account) => {
      if (!account || typeof account !== 'object' || !Object.prototype.hasOwnProperty.call(account, 'switch_token')) {
        return account;
      }
      const safe = { ...account };
      delete safe.switch_token;
      changed = true;
      return safe;
    }).filter(Boolean);
    if (changed) safeSet(ACCOUNTS_KEY, JSON.stringify(safeAccounts));
    return safeAccounts;
  } catch (_) { return []; }
};

const removeAccountFromStorage = (email) => {
  const accounts = getSavedAccounts().filter(a => a.email !== email);
  safeSet(ACCOUNTS_KEY, JSON.stringify(accounts));
  return accounts;
};


/* ─── Icons ─── */
const Icon = {
  verified: <BadgeCheck size={14} strokeWidth={2.5} />,
  wallet: <Wallet size={16} strokeWidth={2} />,
  percent: <Percent size={16} strokeWidth={2} />,
  swap: <ArrowRightLeft size={14} strokeWidth={2} />,
  plus: <Plus size={16} strokeWidth={2} />,
  x: <X size={14} strokeWidth={2} />,
  user: <User size={16} strokeWidth={2} />,
  bell: <Bell size={16} strokeWidth={2} />,
  help: <HelpCircle size={16} strokeWidth={2} />,
  external: <ExternalLink size={12} strokeWidth={2} />,
  logout: <LogOut size={16} strokeWidth={2} />,
  settings: <Settings size={16} strokeWidth={2} />,
  lock: <Lock size={16} strokeWidth={2} />,
};

/* ─── Format balance ─── */
const formatBalance = (val) => {
  if (val === undefined || val === null || val === '—') return { text: '—', num: 0 };
  const num = typeof val === 'string' ? parseFloat(val.replace(/[^\d.,-]/g, '').replace(',', '.')) : val;
  if (!Number.isFinite(num)) return { text: String(val), num: 0 };
  return {
    text: new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', maximumFractionDigits: 2 }).format(num),
    num,
  };
};

const getBalanceClass = (num) => {
  if (num < 0) return 'mb-pdd__balance--negative';
  if (num < 100) return 'mb-pdd__balance--low';
  return 'mb-pdd__balance--positive';
};

/* ═══════════════════════════════════════════════════════════════
   ProfileDropdown Component (JSX / CRA)
   ═══════════════════════════════════════════════════════════════ */
export default function ProfileDropdown({
  user,
  onLogout,
  balance: balanceProp,
  tariffName: tariffProp,
  moduleSettingsUrl,
  navigate,
}) {
  const [open, setOpen] = useState(false);
  const [savedAccounts, setSavedAccounts] = useState([]);
  const [internalBalance, setInternalBalance] = useState('—');
  const [internalTariff, setInternalTariff] = useState('—');
  const wrapRef = useRef(null);
  const { isEnabled: screenLockEnabled } = useContext(ScreenLockContext);

  useEffect(() => { setSavedAccounts(getSavedAccounts()); }, [open]);

  // Auto-load balance
  useEffect(() => {
    if (balanceProp !== undefined) return;
    let cancelled = false;
    (async () => {
      try {
        const d = await fetchBillingJson(billingApiPath('balance'));
        if (!d) throw new Error('Эндпоинты биллинга недоступны');
        const raw = d?.data?.balance ?? d?.balance;
        const v = Number(raw);
        if (!cancelled && Number.isFinite(v)) {
          setInternalBalance(new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', maximumFractionDigits: 2 }).format(v));
        }
      } catch (_) { if (!cancelled) setInternalBalance('—'); }
    })();
    return () => { cancelled = true; };
  }, [balanceProp]);

  // Auto-load tariff
  useEffect(() => {
    if (tariffProp !== undefined) return;
    let cancelled = false;
    (async () => {
      try {
        const d = await fetchBillingJson(billingApiPath('current-tariff'));
        if (!d) throw new Error('Эндпоинты биллинга недоступны');
        const name = d?.data?.name || d?.data?.tariff_name || d?.tariff?.name || null;
        if (!cancelled && name) setInternalTariff(name);
      } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, [tariffProp]);

  const displayName = user?.display_name || user?.name || user?.email || 'Пользователь';
  const email = user?.email || '';
  const avatarColor = getAvatarColor(email);
  const initials = getInitials(displayName, email);
  const isAdmin = Boolean(user?.is_platform_admin);
  const otherAccounts = savedAccounts.filter(a => a.email !== email);
  const { text: balanceText, num: balanceNum } = formatBalance(balanceProp ?? internalBalance);
  const tariff = tariffProp ?? internalTariff;

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  const handleSwitchAccount = useCallback(async (targetEmail) => {
    const accounts = getSavedAccounts();
    const acc = accounts.find(a => a.email === targetEmail);
    const inactivityDays = acc?.switch_inactivity_days ?? 3;
    const requirePassword = acc?.require_password_on_switch || 'after_inactivity';
    const lastLogin = acc?.last_login ? new Date(acc.last_login).getTime() : 0;
    const inactivityMs = inactivityDays * 24 * 60 * 60 * 1000;
    const accountHasPassword = acc?.has_password !== false;
    const needPassword = accountHasPassword && (
      requirePassword === 'always'
      || acc?.quick_login !== true
      || (Date.now() - lastLogin > inactivityMs)
    );

    const loginSwitchFallback = () => {
      window.location.href = `${UAM_URL}/login?switch=${encodeURIComponent(targetEmail)}&return_url=${encodeURIComponent(window.location.href)}`;
    };

    if (needPassword) {
      // Не вызываем logout: при закрытии окна входа текущая сессия должна остаться.
      loginSwitchFallback();
      return;
    }
    try {
      const res = await fetch(`${UAM_URL}/api/uam/v1/switch-with-token`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: targetEmail }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        try {
          const next = getSavedAccounts().map((a) => (
            a.email === targetEmail
              ? { ...a, last_login: new Date().toISOString(), ...(typeof data.user?.has_password === 'boolean' ? { has_password: data.user.has_password } : {}) }
              : a
          ));
          safeSet(ACCOUNTS_KEY, JSON.stringify(next));
        } catch (_) {}
        window.location.reload();
        return;
      }
      if (res.ok) {
        toastAPI?.error?.(
          pickSafeRussianBackendMessage(
            data.error ?? data.message,
            'Не удалось переключить аккаунт. Откроется страница входа.'
          )
        );
      } else {
        toastAPI?.error?.(
          pickSafeRussianBackendMessage(
            data.error ?? data.message,
            pickHttpStatusRussianFallback(res.status)
          )
        );
      }
      setTimeout(loginSwitchFallback, 750);
    } catch (e) {
      toastAPI?.warning?.(
        pickSafeRussianBackendMessage(
          e?.message,
          'Ошибка сети при переключении аккаунта. Откроется страница входа.'
        )
      );
      setTimeout(loginSwitchFallback, 750);
    }
  }, []);

  const handleAddAccount = useCallback(async () => {
    // Без logout: закрытие окна «новый аккаунт» не должно выкидывать из текущего.
    window.location.href = `${UAM_URL}/login?new_account=1&return_url=${encodeURIComponent(window.location.href)}`;
  }, []);

  const handleRemoveAccount = useCallback(async (targetEmail) => {
    try {
      await fetch(`${UAM_URL}/api/uam/v1/revoke-device-account`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: targetEmail,
          revoke_current_session: false,
        }),
      });
    } catch (_) {
      /* local forget still required */
    }
    setSavedAccounts(removeAccountFromStorage(targetEmail));
  }, []);

  return (
    <div ref={wrapRef} className="mb-pdd">
      <button type="button" className="mb-pdd__trigger" onClick={() => setOpen(v => !v)} aria-expanded={open} aria-label="Меню профиля">
        <span
          className={`mb-pdd__trigger-avatar ${user?.avatar_url ? 'mb-pdd__trigger-avatar--has-image' : 'mb-pdd__trigger-avatar--letter'}`}
          style={!user?.avatar_url ? { '--mb-pdd-avatar-bg': avatarColor } : undefined}
        >
          {user?.avatar_url
            ? <img className="mb-pdd__trigger-avatar-img" src={user.avatar_url} alt="" />
            : initials}
        </span>
        <span className="mb-pdd__trigger-info">
          <span className="mb-pdd__trigger-name">{displayName}</span>
          <span className="mb-pdd__trigger-email">{email}</span>
        </span>
      </button>

      {open && (
        <>
          <div className="mb-pdd__backdrop" role="presentation" onClick={() => setOpen(false)} />
          <div className="mb-pdd__panel">
            <div className="mb-pdd__panel-inner">
              {/* Profile card */}
              <div className="mb-pdd__profile-card">
                <span
                  className={`mb-pdd__profile-avatar ${user?.avatar_url ? 'mb-pdd__profile-avatar--has-image' : 'mb-pdd__profile-avatar--letter'}`}
                  style={!user?.avatar_url ? { '--mb-pdd-avatar-bg': avatarColor } : undefined}
                >
                  {user?.avatar_url
                    ? <img className="mb-pdd__profile-avatar-img" src={user.avatar_url} alt="" />
                    : initials}
                </span>
                <div className="mb-pdd__profile-info">
                  <div className="mb-pdd__profile-name-row">
                    <span className="mb-pdd__profile-name">{displayName}</span>
                    <span className="mb-pdd__profile-verified">{Icon.verified}</span>
                    {isAdmin && <span className="mb-pdd__profile-badge mb-pdd__badge--admin">★ Суперадмин</span>}
                  </div>
                  <div className="mb-pdd__profile-card-email">{email}</div>
                </div>
              </div>

              <div className="mb-pdd__divider" />

              {/* Finance cards */}
              <div className="mb-pdd__finance-row">
                <button
                  type="button"
                  className="mb-pdd__finance-card"
                  onClick={() => navigate('/settings?tab=balance')}
                  title="Перейти к балансу"
                >
                  <span className="mb-pdd__finance-label">{Icon.wallet} Баланс</span>
                  <span className={`mb-pdd__finance-value ${getBalanceClass(balanceNum)}`}>{balanceText}</span>
                </button>
                <button
                  type="button"
                  className="mb-pdd__finance-card"
                  onClick={() => navigate('/settings?tab=tariffs')}
                  title="Перейти к тарифам"
                >
                  <span className="mb-pdd__finance-label">{Icon.percent} Тариф</span>
                  <span className="mb-pdd__finance-value mb-pdd__tariff-value">{tariff}</span>
                </button>
              </div>

              <div className="mb-pdd__divider" />

              {/* Accounts */}
              <div className="mb-pdd__section-header">{Icon.swap} Аккаунты</div>
              {otherAccounts.length > 0 && (
                <div className="mb-pdd__account-list">
                  {otherAccounts.slice(0, 4).map(acc => (
                    <button key={acc.email} type="button" className="mb-pdd__account-item" onClick={() => handleSwitchAccount(acc.email)} title={`Переключиться на ${acc.email}`}>
                      <span
                        className="mb-pdd__account-avatar"
                        style={{ '--mb-pdd-avatar-bg': getAvatarColor(acc.email) }}
                      >
                        {getInitials(acc.display_name, acc.email)}
                      </span>
                      <span className="mb-pdd__account-info">
                        <span className="mb-pdd__account-name">{acc.display_name || acc.email}</span>
                        <span className="mb-pdd__account-email">{maskEmail(acc.email)}</span>
                      </span>
                      <button type="button" className="mb-pdd__account-remove" onClick={(e) => { e.stopPropagation(); handleRemoveAccount(acc.email); }} title="Удалить из списка">
                        {Icon.x}
                      </button>
                    </button>
                  ))}
                </div>
              )}
              <button type="button" className="mb-pdd__add-account" onClick={handleAddAccount}>
                <span className="mb-pdd__add-account-icon">{Icon.plus}</span>
                Добавить аккаунт
              </button>

              {/* ── Module actions ── */}
              {moduleSettingsUrl && (
                <>
                  <div className="mb-pdd__divider" />
                  <div className="mb-pdd__menu-links">
                    <button type="button" className="mb-pdd__menu-link" onClick={() => navigate ? navigate(moduleSettingsUrl) : (window.location.href = moduleSettingsUrl)}>
                      <span className="mb-pdd__menu-link-icon">{Icon.settings}</span>
                      Настройки модуля
                    </button>
                  </div>
                </>
              )}

              <div className="mb-pdd__divider" />

              {/* ── External navigation ── */}
              <div className="mb-pdd__section-label">Платформа</div>
              <div className="mb-pdd__menu-links">
                <a className="mb-pdd__menu-link" href={`${UAM_URL}/account`} target="_blank" rel="noopener noreferrer">
                  <span className="mb-pdd__menu-link-icon">{Icon.user}</span>
                  Профиль и безопасность
                  <span className="mb-pdd__external-badge">{Icon.external}</span>
                </a>
                <a className="mb-pdd__menu-link" href={NOTIFICATIONS_APP_INBOX} target="_blank" rel="noopener noreferrer">
                  <span className="mb-pdd__menu-link-icon">{Icon.bell}</span>
                  Центр уведомлений
                  <span className="mb-pdd__external-badge">{Icon.external}</span>
                </a>
                <button type="button" className="mb-pdd__menu-link" onClick={() => navigate('/settings?tab=balance')}>
                  <span className="mb-pdd__menu-link-icon">{Icon.wallet}</span>
                  Кошелек и платежи
                </button>
                <button type="button" className="mb-pdd__menu-link" onClick={() => navigate('/settings?tab=tariffs')}>
                  <span className="mb-pdd__menu-link-icon">{Icon.percent}</span>
                  Тарифы и биллинг
                </button>
                <a className="mb-pdd__menu-link" href={HELP_URL} target="_blank" rel="noopener noreferrer">
                  <span className="mb-pdd__menu-link-icon">{Icon.help}</span>
                  Помощь
                  <span className="mb-pdd__external-badge">{Icon.external}</span>
                </a>
              </div>

              <div className="mb-pdd__divider" />

              <div className="mb-pdd__logout-section">
                {/* Кнопка блокировки — только если screen lock включён в настройках */}
                {screenLockEnabled && (
                  <button
                    type="button"
                    className="mb-pdd__menu-link mb-pdd__menu-link--lock"
                    onClick={() => {
                      setOpen(false);
                      window.dispatchEvent(new CustomEvent('markbase_screen_lock'));
                    }}
                  >
                    <span className="mb-pdd__menu-link-icon">{Icon.lock}</span>
                    Заблокировать экран
                  </button>
                )}

                <button type="button" className="mb-pdd__logout-btn" onClick={onLogout}>
                  <span className="mb-pdd__logout-icon">{Icon.logout}</span>
                  Выйти из аккаунта
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
